import React from 'react';

import { Coords, SpreadGroundImage } from 'editor/src/store/design/types';

import getSpreadGroundImageScale from 'editor/src/util/getSpreadGroundImageScale';
import useFabricUtils from 'editor/src/util/useFabricUtils';

import FabricImageComponent from 'editor/src/component/EditorArea/fabricComponents/FabricImageComponent';
import { CanvasRotation } from 'editor/src/component/EditorArea/types';
import useCanvasRotation from 'editor/src/component/EditorArea/useCanvasRotation';

interface Props {
  imageData: SpreadGroundImage;
  spreadHeight: number;
  spreadWidth: number;
  spreadCoords: Coords;
  zIndex: number;
  canvasRotation: CanvasRotation;
  // When set, the bg/fg image is rendered with a single uniform scale so its
  // natural aspect ratio is preserved even when the spread mm aspect doesn't
  // match the asset's label-rect px aspect. Used by the personalizer SDK
  // preview where natural-looking imagery matters more than pixel-perfect
  // alignment between the design and the label rect.
  uniformScale?: boolean;
}

function SpreadGroundImageElement({
  spreadCoords,
  imageData,
  spreadWidth,
  spreadHeight,
  zIndex,
  canvasRotation,
  uniformScale,
}: Props) {
  const { mm2px } = useFabricUtils();
  const { scaleX: imageScaleX, scaleY: imageScaleY } = getSpreadGroundImageScale(
    imageData,
    spreadWidth,
    spreadHeight,
    mm2px,
    uniformScale,
  );

  const rotatedPoint = useCanvasRotation(
    canvasRotation,
    spreadCoords.left - imageData.left * imageScaleX,
    spreadCoords.top - imageData.top * imageScaleY,
  );

  return (
    <FabricImageComponent
      source={imageData.url}
      crossOrigin="anonymous"
      scaleX={imageScaleX}
      scaleY={imageScaleY}
      left={rotatedPoint.x}
      top={rotatedPoint.y}
      zIndex={zIndex}
      evented={false}
      selectable={false}
      angle={canvasRotation.angleDeg}
    />
  );
}

export default React.memo(SpreadGroundImageElement);
